import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { X, FileText, Languages } from 'lucide-react';
import { DEFAULT_GROUP_POLICY } from '../../constants/policies';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

function PolicyModalContent({ onClose, policy, courseName, customPolicyText, customPolicyTextKr }) {
  // Language toggle state - default to English
  const [language, setLanguage] = useState('en');

  // Use default Group policy if none provided
  const displayPolicy = policy || DEFAULT_GROUP_POLICY;

  // Determine available text for each language
  const englishText = customPolicyText || displayPolicy.description;
  const koreanText = customPolicyTextKr || displayPolicy.description_kr;

  // Check if Korean text is available
  const hasKoreanText = Boolean(koreanText);

  // Use custom policy text if provided, otherwise fall back to base policy description
  const policyDescription = language === 'kr' && hasKoreanText ? koreanText : englishText;

  return (
    <div
      className="flex items-center justify-center p-4 h-full"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal - Fixed height */}
      <div
        className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md h-[420px] sm:h-[450px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#589DD5]" />
            <h2 className="text-base sm:text-lg font-semibold">Course Policy</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 flex flex-col gap-3 sm:gap-4 flex-1 min-h-0">
          {/* Course Info - Fixed */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5 sm:p-3 shrink-0">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Course</p>
            <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">{courseName}</p>
          </div>

          {/* Policy Description - Scrollable */}
          {policyDescription ? (
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-lg p-2.5 sm:p-3 flex flex-col flex-1 min-h-0">
              <div className="flex items-center justify-between mb-1 shrink-0">
                <span className="text-[10px] sm:text-xs font-semibold text-blue-400 uppercase tracking-wider">
                  {displayPolicy.display_name || 'Group'} Policy
                </span>
                {/* Language Toggle - only show if Korean text is available */}
                {hasKoreanText && (
                  <div className="flex items-center gap-1">
                    <Languages className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                    <div className="flex rounded overflow-hidden border border-gray-200 dark:border-gray-600">
                      <button
                        onClick={() => setLanguage('kr')}
                        className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                          language === 'kr'
                            ? 'bg-blue-400 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        KR
                      </button>
                      <button
                        onClick={() => setLanguage('en')}
                        className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                          language === 'en'
                            ? 'bg-blue-400 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        EN
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed overflow-y-auto flex-1">
                {policyDescription}
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 sm:p-3 flex-1 flex items-center justify-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No policy information available for this course.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2 sm:py-2.5 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm sm:text-base font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PolicyModal({ isOpen, onClose, policy, courseName, customPolicyText, customPolicyTextKr }) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex: 200 }}>
        <PolicyModalContent
          onClose={onClose}
          policy={policy}
          courseName={courseName}
          customPolicyText={customPolicyText}
          customPolicyTextKr={customPolicyTextKr}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}
